import React, { useState, useEffect, useRef } from 'react'
import { Send, User, Building, AlertCircle } from 'lucide-react'
import axiosInstance from '../../../services/axiosInstance'
import './styles.scss'

export default function ChatPanel({ studentProfileId, initialText, onClearInitialText, isAdmin = true, refreshTrigger = 0 }) {
  const [messages, setMessages] = useState([])
  const [inputText, setInputText] = useState('')
  const [loading, setLoading] = useState(false)
  const messagesEndRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    if (isAdmin && !studentProfileId) return;
    fetchMessages()
  }, [studentProfileId, isAdmin, refreshTrigger])

  useEffect(() => {
    if (initialText) {
      setInputText(initialText)
      onClearInitialText() // Clear it so it doesn't get re-applied on every render
      if (inputRef.current) {
        inputRef.current.focus()
      }
    }
  }, [initialText, onClearInitialText])

  const fetchMessages = async () => {
    try {
      const endpoint = isAdmin ? `/api/messages/admin/student/${studentProfileId}` : `/api/messages`
      const res = await axiosInstance.get(endpoint)
      if (res.data.success) {
        setMessages(res.data.data)
        scrollToBottom()
      }
    } catch (e) {
      console.error("Failed to fetch messages", e)
    }
  }

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 100)
  }

  const handleSend = async () => {
    if (!inputText.trim()) return

    const tempMsg = {
      id: 'temp-' + Date.now(),
      text: inputText,
      sender: !isAdmin, // if student, sender=true. if admin, sender=false
      time: 'Sending...'
    }

    setMessages(prev => [...prev, tempMsg])
    const payloadText = inputText
    setInputText('')
    scrollToBottom()

    try {
      setLoading(true)
      const endpoint = isAdmin ? `/api/messages/admin/student/${studentProfileId}` : `/api/messages`
      const res = await axiosInstance.post(endpoint, { text: payloadText })
      if (res.data.success) {
        setMessages(prev => prev.map(m => m.id === tempMsg.id ? res.data.data : m))
      }
    } catch (e) {
      console.error("Failed to send message", e)
      setMessages(prev => prev.filter(m => m.id !== tempMsg.id))
      setInputText(payloadText) // put back
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div className="glass-panel chat-panel">
      <div className="chat-header">
        <h3>{isAdmin ? 'Applicant Chat' : 'Admissions Support'}</h3>
        <span className="chat-subtitle">{isAdmin ? 'Communicate directly with the student' : 'Chat with the university admissions team'}</span>
      </div>
      
      <div className="chat-messages">
        {messages.length === 0 ? (
          <div className="empty-chat">
            <AlertCircle size={24} color="#9ca3af" />
            <p>No messages yet.</p>
          </div>
        ) : (
          messages.map(msg => (
            <div key={msg.id} className={`chat-bubble-wrapper ${isAdmin ? (msg.sender ? 'student-msg' : 'admin-msg') : (msg.sender ? 'admin-msg' : 'student-msg')}`}>
              <div className="chat-avatar">
                {isAdmin ? (msg.sender ? <User size={14} /> : <Building size={14} />) : (msg.sender ? <Building size={14} /> : <User size={14} />)}
              </div>
              <div className="chat-bubble">
                {msg.senderName && <span className="chat-sender-name">{msg.senderName}</span>}
                <p>{msg.text}</p>
                <span className="chat-time">{msg.time}</span>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-area">
        <textarea
          ref={inputRef}
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          rows={3}
        />
        <button className="btn-send" onClick={handleSend} disabled={loading || !inputText.trim()}>
          <Send size={18} />
        </button>
      </div>
    </div>
  )
}
